import { Cpu, ExternalLink, TriangleAlert } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useH3Fleet } from "../gpu-fleet/h3Fleet";
import {
  activeRolesForProvider,
  describeTier,
  type ActiveI2vConfig,
  type ActiveTier,
  type TierRole,
  useActiveI2v,
} from "./activeI2v";
import { FleetCard } from "./FleetCard";
import { flattenPricing, formatPrice, priceExtras } from "./pricing";
import { type Capabilities, type Provider, useI2vRegistry } from "./registry";

// The image-to-video provider registry is exported from the composer
// (scripts/export_i2v_registry.py). The page fetches it live from /api/ai-registry and
// falls back to the bundled snapshot (see ./registry) when that endpoint is unreachable.
const NONE = "—";

function durationLabel(caps: Capabilities): string {
  if (caps.supported_durations_seconds.length > 0) {
    return `${caps.supported_durations_seconds.join(", ")}s`;
  }
  if (caps.min_duration_seconds != null && caps.max_duration_seconds != null) {
    return `${caps.min_duration_seconds}–${caps.max_duration_seconds}s`;
  }
  return NONE;
}

function resolutionsLabel(caps: Capabilities): string {
  return caps.supported_resolutions.length > 0
    ? caps.supported_resolutions.join(", ")
    : "model-native";
}

function CapabilityBadges({ caps }: { caps: Capabilities }) {
  const flags: Array<[string, boolean]> = [
    ["Native audio", caps.supports_native_audio],
    ["Reference images", caps.supports_reference_images],
    ["First/last frame", caps.supports_first_last_frame],
    ["Negative prompt", caps.supports_negative_prompt],
    ["Camera control", caps.supports_camera_control],
    ["Seed", caps.supports_seed],
  ];
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map(([label, on]) => (
        <Badge
          key={label}
          variant={on ? "default" : "outline"}
          className={on ? "" : "text-muted-foreground"}
        >
          {label}
          {label === "Reference images" && on && caps.max_reference_images > 0
            ? ` (${caps.max_reference_images})`
            : ""}
        </Badge>
      ))}
    </div>
  );
}

function PricingTable({ provider }: { provider: Provider }) {
  const rows = flattenPricing(provider.pricing.models);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No per-call price — routed via another pool or self-hosted.
      </p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Model</TableHead>
          <TableHead>Tier / variant</TableHead>
          <TableHead>Unit</TableHead>
          <TableHead>Price</TableHead>
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({ modelId, variant, row }) => {
          const extras = priceExtras(row);
          return (
            <TableRow key={`${modelId}:${variant}`}>
              <TableCell className="font-mono text-xs">{modelId}</TableCell>
              <TableCell className="text-muted-foreground">{variant}</TableCell>
              <TableCell>{row.unit}</TableCell>
              <TableCell>
                <span>{formatPrice(row)}</span>
                {extras ? (
                  <span className="block text-xs text-muted-foreground">
                    {extras}
                  </span>
                ) : null}
              </TableCell>
              <TableCell>
                {row.source ? (
                  <a
                    href={row.source}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    title={row.source}
                  >
                    <ExternalLink className="size-3.5" />
                  </a>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const TIER_BADGE_LABELS: Record<TierRole, string> = {
  primary: "Active · primary (hero)",
  secondary: "Active · secondary (bulk)",
};

const TIER_BADGE_TITLES: Record<TierRole, string> = {
  primary:
    "The composer's primary i2v route for selected hero scenes " +
    "currently routes to this provider in this environment",
  secondary:
    "The composer's secondary i2v route for animated bulk scenes currently " +
    "routes to this provider in this environment",
};

function ActiveTierBadges({ roles }: { roles: TierRole[] }) {
  return (
    <>
      {roles.map((role) => (
        <Badge
          key={role}
          variant={role === "primary" ? "default" : "secondary"}
          title={TIER_BADGE_TITLES[role]}
        >
          {TIER_BADGE_LABELS[role]}
        </Badge>
      ))}
    </>
  );
}

function TierSummary({ label, tier }: { label: string; tier: ActiveTier }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono text-xs" title={tier.unresolved_reason}>
        {describeTier(tier)}
        {tier.model_source === "composer_default" ? (
          <span className="ml-1 font-sans text-muted-foreground">
            (model: composer default)
          </span>
        ) : null}
      </dd>
    </div>
  );
}

// What is ACTUALLY running in this environment right now — the tier resolution the
// dashboard-api reads from the Step-Function-pinned composer job definition.
function ActiveConfigCard({ active }: { active: ActiveI2vConfig }) {
  const { primary, secondary } = active.tiers;
  const revision = active.job_definition;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">Active configuration</CardTitle>
          <Badge variant="default" className="capitalize">
            {active.environment_label}
          </Badge>
          <Badge
            variant="outline"
            className="font-mono text-xs font-normal text-muted-foreground"
            title="AWS environment name and the composer Batch job-definition revision this reflects"
          >
            {active.environment} · {revision.name}:{revision.resolved_revision}
          </Badge>
          {revision.revision_drift ? (
            <Badge
              variant="destructive"
              title={`The Step Function pins revision ${revision.resolved_revision} but the latest ACTIVE revision is ${revision.latest_active_revision} — a deploy or pin update likely went half-way`}
            >
              <TriangleAlert aria-hidden className="size-3" />
              pin ≠ latest (:{revision.latest_active_revision})
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <TierSummary label="Primary (hero i2v)" tier={primary} />
          <TierSummary label="Secondary (bulk i2v)" tier={secondary} />
        </dl>
        {secondary.same_as_primary ? (
          <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
            <TriangleAlert aria-hidden className="size-3.5 shrink-0" />
            Secondary route resolves to the same provider and model as the
            primary route — bulk scenes are billed at the standard rate.
          </p>
        ) : null}
        {active.notes.length > 0 ? (
          <ul className="list-disc pl-4 text-xs text-muted-foreground">
            {active.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ProviderCard({
  provider,
  activeRoles,
}: {
  provider: Provider;
  activeRoles: TierRole[];
}) {
  const caps = provider.capabilities;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            {provider.display_name}
            <Badge variant="outline" className="font-mono text-xs font-normal">
              {provider.id}
            </Badge>
            <ActiveTierBadges roles={activeRoles} />
          </CardTitle>
          <Badge variant="secondary">
            {provider.concurrency_default > 0
              ? `concurrency ${provider.concurrency_default}`
              : "concurrency: unset"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Resolutions</dt>
            <dd>{resolutionsLabel(caps)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Duration</dt>
            <dd>{durationLabel(caps)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Aspect ratios</dt>
            <dd>{caps.supported_aspect_ratios.join(", ") || NONE}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Output FPS</dt>
            <dd>{caps.output_fps ?? NONE}</dd>
          </div>
        </dl>
        <CapabilityBadges caps={caps} />
        <PricingTable provider={provider} />
      </CardContent>
    </Card>
  );
}

export const AiSettingsPage = () => {
  const { registry, source, isLoading } = useI2vRegistry();
  const { active } = useActiveI2v();
  const { fleet } = useH3Fleet();
  const effective = registry.pricing_effective_date ?? "unknown";
  return (
    <div className="space-y-4 p-6">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Cpu className="size-5 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">AI Settings</h1>
          {isLoading ? null : (
            <Badge
              variant={source === "live" ? "default" : "outline"}
              className={source === "live" ? "" : "text-muted-foreground"}
              title={
                source === "live"
                  ? "Fetched live from the composer via /api/ai-registry"
                  : "Live endpoint unavailable — showing the bundled snapshot"
              }
            >
              {source === "live" ? "Live" : "Snapshot"}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          The image-to-video provider registry the Echo pipeline runs on — every
          provider, its models, per-second / per-clip / per-token pricing,
          default concurrency, and capabilities. Sourced from the composer
          (pricing effective {effective}); prices in {registry.currency}.
        </p>
        <p className="text-xs text-muted-foreground">
          Concurrency is the provider-default cap; deployments override it per
          provider via the{" "}
          <code className="font-mono">{registry.concurrency_override_env}</code>{" "}
          env.
        </p>
      </div>

      {active ? <ActiveConfigCard active={active} /> : null}
      {fleet ? <FleetCard fleet={fleet} /> : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Providers</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Concurrency</TableHead>
                <TableHead>Resolutions</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Audio</TableHead>
                <TableHead>Models</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {registry.providers.map((provider) => (
                <TableRow key={provider.id}>
                  <TableCell className="font-medium">
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      {provider.display_name}
                      <ActiveTierBadges
                        roles={activeRolesForProvider(active, provider.id)}
                      />
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {provider.id}
                  </TableCell>
                  <TableCell>{provider.concurrency_default || NONE}</TableCell>
                  <TableCell>
                    {resolutionsLabel(provider.capabilities)}
                  </TableCell>
                  <TableCell>{durationLabel(provider.capabilities)}</TableCell>
                  <TableCell>
                    {provider.capabilities.supports_native_audio ? "Yes" : "No"}
                  </TableCell>
                  <TableCell>
                    {Object.keys(provider.pricing.models ?? {}).length || NONE}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {registry.providers.map((provider) => (
        <ProviderCard
          key={provider.id}
          provider={provider}
          activeRoles={activeRolesForProvider(active, provider.id)}
        />
      ))}
    </div>
  );
};

AiSettingsPage.path = "/ai-settings";
